import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Archive, Pencil, Plus, ReceiptText } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { FormularioGasto, type Gasto } from "@/paginas/interno/Gastos"

/**
 * «Servicios y precios» en la ficha del proveedor (04/11, pedido del usuario): qué ofrece
 * y a qué precio. Son sus gastos del catálogo —`gastos` con su `proveedor_id`—, los
 * mismos que la orden de pago enseña al elegirlo y los que «Gastos y categorías» lista.
 * Un solo catálogo: el precio se corrige en un sitio.
 *
 * ⚠ **La MISMA clave de consulta que el formulario de la orden**
 * (`["gastos", "proveedor", id]`, sólo los activos): comparten caché, y lo que se añade
 * aquí sale allí sin otra petición.
 *
 * ⚠ **Sin poder ver el gasto de la agencia sólo salen los nombres** (`gastos` del perfil):
 * el precio es lo que la agencia paga, y lo abren `gasto:ver` u `orden_pago:crear`, igual
 * que la lista de la orden. Cambiar, añadir y archivar piden `gasto:administrar`.
 */
export function ServiciosDelProveedor({
  proveedorId,
  proveedorNombre,
  nombres,
}: {
  proveedorId: number
  proveedorNombre: string
  /** Los nombres que trae el perfil, para quien no puede ver los precios. */
  nombres: string[]
}) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeVer = tienePermiso(usuario, "gasto:ver") || tienePermiso(usuario, "orden_pago:crear")
  const puedeEditar = tienePermiso(usuario, "gasto:administrar")
  const [editando, setEditando] = useState<Gasto | "nuevo" | null>(null)
  const [archivando, setArchivando] = useState<number | null>(null)

  const servicios = useQuery({
    queryKey: ["gastos", "proveedor", proveedorId],
    queryFn: () =>
      api.get<Gasto[]>(`/api/v1/gastos?proveedor_id=${proveedorId}&activo=true&limit=100`),
    enabled: puedeVer,
  })

  const archivar = useMutation({
    mutationFn: (id: number) => api.patch(`/api/v1/gastos/${id}`, { activo: false }),
    onSuccess: () => {
      setArchivando(null)
      clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
    },
  })

  if (!puedeVer) {
    if (nombres.length === 0) return null
    return (
      <section className="space-y-1">
        <h3 className="text-sm font-semibold">Servicios que ofrece</h3>
        <ul className="flex flex-wrap gap-2">
          {nombres.map((nombre) => (
            <li key={nombre}>
              <Insignia tono="neutro" icono={<ReceiptText size={12} />}>
                {nombre}
              </Insignia>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Servicios y precios</h3>
        {puedeEditar && editando === null && (
          <Boton tamano="sm" onClick={() => setEditando("nuevo")}>
            <Plus size={14} aria-hidden={true} /> Agregar servicio
          </Boton>
        )}
      </div>

      {editando !== null && (
        <div className="tarjeta p-3">
          <FormularioGasto
            key={editando === "nuevo" ? "nuevo" : editando.id}
            gasto={editando === "nuevo" ? null : editando}
            proveedorFijo={{ id: proveedorId, nombre: proveedorNombre }}
            onCerrar={() => setEditando(null)}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
              clienteQuery.invalidateQueries({ queryKey: ["proveedor-perfil", proveedorId] })
              setEditando(null)
            }}
          />
        </div>
      )}

      {servicios.isPending && <EstadoCarga etiqueta="Cargando sus servicios…" filas={2} />}
      {servicios.isError && (
        <EstadoError
          titulo="No se pudieron cargar sus servicios."
          error={servicios.error}
          onReintentar={() => servicios.refetch()}
        />
      )}
      {servicios.data && servicios.data.length === 0 && editando === null && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no tiene. Agrega lo que ofrece con su precio y saldrá al redactarle una orden de
          pago.
        </p>
      )}

      {servicios.data && servicios.data.length > 0 && (
        <ul className="space-y-1">
          {servicios.data.map((servicio) => (
            <li
              key={servicio.id}
              className="tarjeta flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm"
              data-testid={`servicio-${servicio.id}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block font-medium break-words">{servicio.nombre}</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {[
                    servicio.categoria ?? "Sin categoría",
                    servicio.tipo_gasto === "FIJO" ? "Fijo" : "Variable",
                  ].join(" · ")}
                </span>
              </span>
              <strong className="tabular-nums">
                {servicio.monto_referencia
                  ? `${monto(servicio.monto_referencia)} ${servicio.divisa ?? ""}`
                  : "Sin precio"}
              </strong>
              {puedeEditar &&
                editando === null &&
                (archivando === servicio.id ? (
                  <span className="flex items-center gap-1 text-xs">
                    ¿Archivar?
                    <Boton
                      tamano="sm"
                      variante="peligro"
                      disabled={archivar.isPending}
                      onClick={() => archivar.mutate(servicio.id)}
                    >
                      Sí, archivar
                    </Boton>
                    <Boton tamano="sm" onClick={() => setArchivando(null)}>
                      No
                    </Boton>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Editar ${servicio.nombre}`}
                      onClick={() => setEditando(servicio)}
                      className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                    >
                      <Pencil size={14} aria-hidden={true} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Archivar ${servicio.nombre}`}
                      onClick={() => setArchivando(servicio.id)}
                      className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                    >
                      <Archive size={14} aria-hidden={true} />
                    </button>
                  </span>
                ))}
            </li>
          ))}
        </ul>
      )}
      {archivar.error instanceof ErrorApi && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {archivar.error.message}
        </p>
      )}
    </section>
  )
}
